import { Component, OnInit, ViewEncapsulation } from '@angular/core'
import { AppUserDto, UserReviewClient } from 'src/app/shared/services/ppv.api'

export interface AdminUserRow {
   id: string | null
   displayName: string
   accessLevel: 'StandardAdmin' | 'SuperAdmin'
}

interface PermissionEntry {
   label: string
   level: 'both' | 'super'
}

@Component({
   selector: 'app-role-permissions',
   templateUrl: './role-permissions.component.html',
   styleUrls: ['./role-permissions.component.scss'],
   standalone: false,
   encapsulation: ViewEncapsulation.None,
})
export class RolePermissionsComponent implements OnInit {
   loading = false
   adminUsers: AdminUserRow[] = []

   readonly permissionMatrix: PermissionEntry[] = [
      { label: 'View reviews & manage review statuses', level: 'both' },
      { label: 'View reporting dashboard', level: 'both' },
      { label: 'Export data & view analytics', level: 'both' },
      { label: 'Transfer users between locations', level: 'super' },
      { label: 'High-level overrides', level: 'super' },
      { label: 'Control deferral requests', level: 'super' },
      { label: 'Manage cycles', level: 'super' },
      { label: 'Add & remove administrators', level: 'super' },
   ]

   // ── Dialog state ──────────────────────────────────────────────────────────
   editDialogAdmin: AdminUserRow | null = null
   dialogSaving = false

   // ── Autocomplete state ────────────────────────────────────────────────────
   userSearchQuery = ''
   userSearchResults: AppUserDto[] = []
   selectedUserForAdmin: AppUserDto | null = null
   showUserDropdown = false
   searchLoading = false
   private searchDebounce: any = null

   // ── Feedback ──────────────────────────────────────────────────────────────
   actionSuccess: string | null = null
   actionError: string | null = null

   private readonly AVATAR_PALETTES = [
      { bg: '#e0eef6', fg: '#0374b6' },
      { bg: '#daf3f2', fg: '#26b5ae' },
      { bg: '#ffefcc', fg: '#976a12' },
      { bg: '#fdebe4', fg: '#f05d44' },
      { bg: '#e9dee9', fg: '#831183' },
      { bg: '#fae8e8', fg: '#d80c47' },
   ]

   constructor(private userReviewClient: UserReviewClient) {}

   ngOnInit(): void {
      this.loadAdmins()
   }

   // ── Data ──────────────────────────────────────────────────────────────────

   loadAdmins(): void {
      this.loading = true
      this.userReviewClient.getAdminUsers().subscribe({
         next: (data) => {
            this.adminUsers = data.map((u) => ({
               id: u.userId,
               displayName: u.displayName,
               accessLevel: u.accessLevel as AdminUserRow['accessLevel'],
            }))
            this.loading = false
         },
         error: () => {
            this.loading = false
            this.showError('Failed to load admin users.')
         },
      })
   }

   // ── Dialog ────────────────────────────────────────────────────────────────

   openAddAdmin(): void {
      this.editDialogAdmin = { id: null, displayName: '', accessLevel: 'StandardAdmin' }
      this.userSearchQuery = ''
      this.userSearchResults = []
      this.selectedUserForAdmin = null
      this.showUserDropdown = false
   }

   openEditAdmin(admin: AdminUserRow): void {
      this.editDialogAdmin = { ...admin }
      this.selectedUserForAdmin = null
      this.userSearchQuery = ''
      this.showUserDropdown = false
   }

   closeEditAdmin(): void {
      this.editDialogAdmin = null
      this.dialogSaving = false
      this.selectedUserForAdmin = null
      this.userSearchQuery = ''
      this.userSearchResults = []
      this.showUserDropdown = false
      clearTimeout(this.searchDebounce)
   }

   saveAdmin(): void {
      if (!this.editDialogAdmin) return
      if (!this.editDialogAdmin.id && !this.selectedUserForAdmin) return

      this.dialogSaving = true

      const payload = {
         userId: this.editDialogAdmin.id ?? this.selectedUserForAdmin!.id,
         displayName: this.editDialogAdmin.id ? this.editDialogAdmin.displayName : this.selectedUserForAdmin!.displayName,
         accessLevel: this.editDialogAdmin.accessLevel,
      }

      this.userReviewClient.saveAdminUser(payload).subscribe({
         next: () => {
            this.dialogSaving = false
            this.closeEditAdmin()
            this.loadAdmins()
            this.showSuccess('Admin user saved successfully.')
         },
         error: () => {
            this.dialogSaving = false
            this.showError('Failed to save admin user.')
         },
      })
   }

   removeAdmin(admin: AdminUserRow): void {
      if (!admin.id) return
      this.dialogSaving = true
      this.userReviewClient.removeAdminUser(admin.id).subscribe({
         next: () => {
            this.dialogSaving = false
            this.closeEditAdmin()
            this.adminUsers = this.adminUsers.filter((a) => a.id !== admin.id)
            this.showSuccess('Admin user removed.')
         },
         error: () => {
            this.dialogSaving = false
            this.showError('Failed to remove admin user.')
         },
      })
   }

   // ── Autocomplete ──────────────────────────────────────────────────────────

   onUserSearchInput(): void {
      const q = this.userSearchQuery.trim()

      if (this.selectedUserForAdmin) return

      if (q.length < 2) {
         this.userSearchResults = []
         this.showUserDropdown = false
         this.searchLoading = false
         clearTimeout(this.searchDebounce)
         return
      }

      // Debounce so we don't fire on every keystroke
      clearTimeout(this.searchDebounce)
      this.searchLoading = true
      this.searchDebounce = setTimeout(() => {
         const existingAdminIds = new Set(this.adminUsers.map((a) => a.id))
         this.userReviewClient.getAppUsers().subscribe({
            next: (users) => {
               this.userSearchResults = users
                  .filter((u) => !existingAdminIds.has(u.id) && u.displayName?.toLowerCase().includes(q.toLowerCase()))
                  .slice(0, 8)
               this.showUserDropdown = true
               this.searchLoading = false
            },
            error: () => {
               this.searchLoading = false
               this.showError('Failed to search users.')
            },
         })
      }, 300)
   }

   selectUserForAdmin(user: AppUserDto): void {
      this.selectedUserForAdmin = user
      this.userSearchQuery = user.displayName
      this.showUserDropdown = false
      this.userSearchResults = []
      clearTimeout(this.searchDebounce)
   }

   clearSelectedUser(): void {
      this.selectedUserForAdmin = null
      this.userSearchQuery = ''
      this.userSearchResults = []
      this.showUserDropdown = false
   }

   onSearchBlur(): void {
      // Small delay so mousedown on dropdown item fires first
      setTimeout(() => {
         this.showUserDropdown = false
      }, 150)
   }

   // ── Avatar helpers ────────────────────────────────────────────────────────

   getInitials(name: string): string {
      if (!name) return '?'
      const parts = name.trim().split(/\s+/)
      return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name.substring(0, 2).toUpperCase()
   }

   getAvatarBg(name: string): string {
      return this.AVATAR_PALETTES[this.hashName(name) % this.AVATAR_PALETTES.length].bg
   }

   getAvatarColor(name: string): string {
      return this.AVATAR_PALETTES[this.hashName(name) % this.AVATAR_PALETTES.length].fg
   }

   private hashName(name: string): number {
      if (!name) return 0
      return name.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0)
   }

   private showSuccess(msg: string): void {
      this.actionSuccess = msg
      this.actionError = null
      setTimeout(() => (this.actionSuccess = null), 3000)
   }

   private showError(msg: string): void {
      this.actionError = msg
      setTimeout(() => (this.actionError = null), 5000)
   }
}
